import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link2, Loader2, Save, CheckCircle2, AlertTriangle, Server } from 'lucide-react'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import apiClient from '@/api/client'
import { useToast } from '@/hooks/use-toast'
import type { APIResponse, PublicLinksConfig } from '@/types'

/**
 * Admin → Settings → Public links.
 *
 * Single source of truth for the customer-facing base URL used by:
 *   • Loyalty QR codes (per-receipt + shop-wide claim)
 *   • Marketing QR codes (menu, promo, social links)
 *   • The public info page `/p/loyalty`
 *
 * The status badge tells the owner at a glance whether their URL is
 * actually reachable from a customer's phone:
 *
 *   • Green  — set in DB and looks scannable (a domain, not localhost)
 *   • Blue   — overridden by the server's MARKETING_QR_REDIRECT_BASE
 *              env var (UI value is ignored until ops removes the env)
 *   • Yellow — auto-detected fallback (the URL is localhost / LAN IP,
 *              which customers outside this network can't reach)
 */
export function AdminPublicLinksSettings() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  const linksQuery = useQuery<APIResponse<PublicLinksConfig>>({
    queryKey: ['public-links'],
    queryFn: () => apiClient.getPublicLinks(),
    staleTime: 60_000,
  })

  const [draft, setDraft] = useState<string>('')
  useEffect(() => {
    if (linksQuery.data?.data) {
      setDraft(linksQuery.data.data.public_base_url ?? '')
    }
  }, [linksQuery.data])

  const saveMutation = useMutation({
    mutationFn: (url: string) => apiClient.updatePublicLinks(url),
    onSuccess: () => {
      toast({ title: 'Public URL saved' })
      queryClient.invalidateQueries({ queryKey: ['public-links'] })
    },
    onError: (err) => {
      const msg = err instanceof Error ? err.message : String(err)
      toast({ title: 'Could not save', description: msg, variant: 'destructive' })
    },
  })

  const data = linksQuery.data?.data
  const resolved = data?.resolved
  const envOverride = resolved?.source === 'env'
  const scannable = resolved?.is_scannable === true

  return (
    <Card id="public-links" className="space-y-5 p-5">
      <div className="flex items-center gap-3 border-b border-border/60 pb-3">
        <Link2 className="h-5 w-5 text-primary" aria-hidden />
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold">Public website URL</h3>
          <p className="text-xs text-muted-foreground">
            The address your customers reach when they scan any QR code from your restaurant.
            Used by loyalty, marketing QR codes, and the public info page.
          </p>
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="public-base-url">Public URL</Label>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-start">
          <div className="flex-1">
            <Input
              id="public-base-url"
              type="url"
              inputMode="url"
              autoComplete="url"
              placeholder="https://chunkyard.bhookly.com"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              disabled={envOverride}
            />
            <p className="mt-1.5 text-[11px] leading-relaxed text-muted-foreground">
              Use your live domain (e.g. <code className="font-mono">https://yourname.bhookly.com</code>).
              Leave blank to fall back to auto-detection.
            </p>
          </div>
          <Button
            type="button"
            onClick={() => saveMutation.mutate(draft.trim())}
            disabled={saveMutation.isPending || envOverride}
            className="gap-1.5 sm:mt-0"
          >
            {saveMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            Save
          </Button>
        </div>
      </div>

      {/* Status row — green / yellow / blue */}
      {linksQuery.isLoading ? (
        <div className="flex h-12 items-center justify-center">
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        </div>
      ) : resolved ? (
        <StatusBadge envOverride={envOverride} scannable={scannable} url={resolved.url} />
      ) : null}

      {/* "How is this used?" expander */}
      <details className="rounded-md border border-border/60 bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
        <summary className="cursor-pointer font-medium text-foreground">
          How is this URL used?
        </summary>
        <ul className="mt-2 space-y-1.5 pl-4 leading-relaxed">
          <li>
            <strong>Loyalty receipt QR</strong> — encoded into the "save this visit" QR printed
            on every paid receipt.
          </li>
          <li>
            <strong>Loyalty poster QR</strong> — encoded into the shop-wide QR on counter posters
            and table tents.
          </li>
          <li>
            <strong>Marketing QR codes</strong> — base URL prepended to every dynamic QR slug
            (menu, promo, social links).
          </li>
          <li>
            <strong>Public info page</strong> — the URL of your customer-facing loyalty FAQ at{' '}
            <code className="font-mono">/p/loyalty</code>.
          </li>
        </ul>
      </details>
    </Card>
  )
}

function StatusBadge({
  envOverride,
  scannable,
  url,
}: {
  envOverride: boolean
  scannable: boolean
  url: string
}) {
  if (envOverride) {
    return (
      <div className="flex items-start gap-2.5 rounded-md border border-sky-300 bg-sky-50 px-3 py-2.5 text-xs text-sky-900 dark:border-sky-800 dark:bg-sky-950/50 dark:text-sky-100">
        <Server className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
        <div className="min-w-0">
          <div className="font-semibold">Set via server environment variable</div>
          <div className="mt-0.5 break-all">
            Effective URL: <code className="font-mono">{url || '—'}</code>
          </div>
          <div className="mt-0.5 text-sky-800/80 dark:text-sky-200/80">
            UI changes here are ignored until <code className="font-mono">MARKETING_QR_REDIRECT_BASE</code> is unset on the server.
          </div>
        </div>
      </div>
    )
  }
  if (scannable) {
    return (
      <div className="flex items-start gap-2.5 rounded-md border border-emerald-300 bg-emerald-50 px-3 py-2.5 text-xs text-emerald-900 dark:border-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-100">
        <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
        <div className="min-w-0">
          <div className="font-semibold">Fully scannable</div>
          <div className="mt-0.5 break-all">
            Customers will reach <code className="font-mono">{url}</code> when they scan your QR codes.
          </div>
        </div>
      </div>
    )
  }
  return (
    <div className="flex items-start gap-2.5 rounded-md border border-amber-300 bg-amber-50 px-3 py-2.5 text-xs text-amber-900 dark:border-amber-800 dark:bg-amber-950/50 dark:text-amber-100">
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
      <div className="min-w-0">
        <div className="font-semibold">Auto-detected — customers outside your network can't reach this</div>
        <div className="mt-0.5 break-all">
          Detected URL: <code className="font-mono">{url || '—'}</code>
        </div>
        <div className="mt-0.5 text-amber-800/80 dark:text-amber-200/80">
          Set your live public URL above before printing posters or processing real orders.
        </div>
      </div>
    </div>
  )
}
